import React, { useState, useEffect } from 'react';
import { Tablist, Tab, Pane, TextInput, Button, toaster, Dialog } from 'evergreen-ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import './UserDashboard.css';
import BookedTickets from './BookedTickets';
import ShippingAddressForm from './ShippingAddressForm';
import OrderHistory from './OrderHistory'; // Import OrderHistory

function UserDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [userData, setUserData] = useState({
    id: '',
    username: '',
    email: '',
    phonenumber: '',
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showTokenExpiredDialog, setShowTokenExpiredDialog] = useState(false);

  const tabs = [
    'User Profile',
    'Change Password',
    'Booked Tickets',
    'Shipping Address',
    'Order History', // Add Order History tab
    'Notifications',
    'Support/Contact Us',
    'Reviews & Feedback',
  ];

  useEffect(() => {
    const fetchUserData = async () => {
      const token = location.state?.token;
      if (!token) {
        toaster.danger('Please log in to access this page');
        navigate('/signin');
        return;
      }

      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          setShowTokenExpiredDialog(true);
          return;
        }

        if (!decodedToken || !decodedToken.id) {
          throw new Error('Invalid Token');
        }

        const userId = decodedToken.id;
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch user data');
        }

        const data = await response.json();
        setUserData({
          id: data.id,
          username: data.username,
          email: data.email,
          phonenumber: data.phonenumber,
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
        if (error.message !== 'Token has expired, please log in again.') {
          toaster.danger(error.message);
        }
      }
    };

    fetchUserData();
  }, [location.state, navigate]);

  const handleProfileUpdate = async () => {
    try {
      const token = location.state?.token;
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        setShowTokenExpiredDialog(true);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/users/${userData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update profile');
      }

      toaster.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toaster.danger(error.message);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmNewPassword) {
      toaster.danger('Passwords do not match');
      return;
    }

    try {
      const token = location.state?.token;
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        setShowTokenExpiredDialog(true);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/users/${userData.id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to change password');
      }

      toaster.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toaster.danger(error.message);
    }
  };

  const handleAddressAdded = () => {
    toaster.success('Shipping address added successfully');
  };

  const renderTabContent = () => {
    switch (selectedIndex) {
      case 0:
        return (
          <Pane display="flex" flexDirection="column" gap={16}>
            <Pane display="flex" alignItems="center" gap={12}>
              <span className="input-label">Username:</span>
              <TextInput
                width={300}
                placeholder="Username"
                value={userData.username}
                onChange={(e) => setUserData({ ...userData, username: e.target.value })}
              />
            </Pane>
            <Pane display="flex" alignItems="center" gap={12}>
              <span className="input-label">Email:</span>
              <TextInput
                width={300}
                placeholder="Email"
                value={userData.email}
                onChange={(e) => setUserData({ ...userData, email: e.target.value })}
              />
            </Pane>
            <Pane display="flex" alignItems="center" gap={12}>
              <span className="input-label">Phone Number:</span>
              <TextInput
                width={300}
                placeholder="Phone Number"
                value={userData.phonenumber}
                onChange={(e) => setUserData({ ...userData, phonenumber: e.target.value })}
              />
            </Pane>
            <Button appearance="primary" onClick={handleProfileUpdate}>
              Update Profile
            </Button>
          </Pane>
        );
      case 1:
        return (
          <Pane display="flex" flexDirection="column" gap={16}>
            <Pane display="flex" alignItems="center" gap={12}>
              <span className="input-label">Current Password:</span>
              <TextInput
                width={300}
                placeholder="Current Password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </Pane>
            <Pane display="flex" alignItems="center" gap={12}>
              <span className="input-label">New Password:</span>
              <TextInput
                width={300}
                placeholder="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Pane>
            <Pane display="flex" alignItems="center" gap={12}>
              <span className="input-label">Confirm Password:</span>
              <TextInput
                width={300}
                placeholder="Confirm New Password"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
            </Pane>
            <Button appearance="primary" onClick={handleChangePassword}>
              Change Password
            </Button>
          </Pane>
        );
      case 2:
        return <BookedTickets userEmail={userData.email} userPhone={userData.phonenumber} />;
      case 3:
        return <ShippingAddressForm onAddressAdded={handleAddressAdded} />;
      case 4:
        return <OrderHistory userEmail={userData.email} userPhone={userData.phonenumber} />; // Render OrderHistory
      default:
        return null;
    }
  };

  return (
    <div className="user_dashboard_container">
      <Dialog
        isShown={showTokenExpiredDialog}
        title="Session Expired"
        onCloseComplete={() => navigate('/')}
        confirmLabel="Sign In"
        cancelLabel="Return to Home"
        onConfirm={() => {
          localStorage.removeItem('token');
          navigate('/login');
        }}
        onCancel={() => {
          localStorage.removeItem('token');
          navigate('/');
        }}
      >
        Your session has expired due to token timeout. Please sign in again to continue or return to the home page.
      </Dialog>

      <div className="usr_top">
        <p style={{ color: '#000' }}>{userData.username}</p>
        <button
          onClick={() => {
            localStorage.removeItem('token');
            navigate('/');
          }}
        >
          Logout
        </button>
      </div>

      <div className="usr_bottom">
        <div className="tablist_container">
          <Tablist>
            {tabs.map((tab, index) => (
              <Tab
                key={tab}
                id={tab}
                onSelect={() => setSelectedIndex(index)}
                isSelected={index === selectedIndex}
                aria-controls={`panel-${tab}`}
              >
                {tab}
              </Tab>
            ))}
          </Tablist>
        </div>

        <div className="pane_container">
          <Pane
            key={tabs[selectedIndex]}
            id={`panel-${tabs[selectedIndex]}`}
            role="tabpanel"
            aria-labelledby={tabs[selectedIndex]}
            padding={16}
          >
            {renderTabContent()}
          </Pane>
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;